import { useEffect, useRef, useState } from "react";
import { MessageSquareText, X, Send, Loader2, Bot, User, MessageCircle } from "lucide-react";
import { SITE, WHATSAPP_URL } from "@/config/site";
import { trackEvent, EVENTS } from "@/utils/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "cs_chat_session";

const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const GREETING = {
  role: "assistant",
  text: "Hola, soy el asistente de DentalAi. Puedo contarte cómo reducimos los no-shows en clínicas dentales o resolver tus dudas sobre el servicio.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const started = useRef(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    if (!started.current) {
      started.current = true;
      trackEvent(EVENTS.CHAT_USE);
    }
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);

    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("chat request failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const partial = acc;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: partial.replace("[WHATSAPP]", "").trim(), whatsapp: partial.includes("[WHATSAPP]") };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Ahora mismo no puedo responder. Te atendemos directamente por WhatsApp.",
          whatsapp: true,
        };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="chat-open-button"
        aria-label={open ? "Cerrar asistente" : "Abrir asistente virtual"}
        className="fixed bottom-5 left-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-navy text-white shadow-[0_12px_28px_-8px_rgba(11,31,58,0.55)] transition-all duration-200 hover:scale-105 hover:bg-navy-light"
        onClick={() => setOpen(!open)}
      >
        {open ? <X className="h-6 w-6" /> : <MessageSquareText className="h-6 w-6" strokeWidth={1.8} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          role="dialog"
          aria-label="Asistente virtual de DentalAi"
          className="fixed bottom-[84px] left-4 z-50 flex h-[520px] max-h-[calc(100dvh-110px)] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-[#E4E9F2] bg-white shadow-[0_24px_60px_-16px_rgba(11,31,58,0.35)] sm:left-5"
        >
          <div className="flex items-center gap-3 bg-navy px-5 py-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10">
              <Bot className="h-5 w-5 text-white" strokeWidth={1.8} />
            </span>
            <div>
              <p className="font-heading text-sm font-bold text-white">Asistente DentalAi</p>
              <p className="text-[11px] text-[#9FB8D8]">Responde al instante · IA con escalado humano</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-platinum/50 p-4">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-message-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "rounded-br-sm bg-navy text-white"
                      : "rounded-bl-sm border border-[#E8EDF5] bg-white text-navy shadow-sm"
                  }`}
                >
                  {msg.text || (
                    <Loader2 className="h-4 w-4 animate-spin text-steel" aria-label="Escribiendo" />
                  )}
                  {msg.whatsapp && (
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="chat-whatsapp-button"
                      className="mt-2.5 flex items-center justify-center gap-2 rounded-lg bg-recover px-3 py-2 text-xs font-bold text-white transition-colors hover:brightness-95"
                      onClick={() => trackEvent(EVENTS.WHATSAPP_CLICK, { ubicacion: "chatbot" })}
                    >
                      <MessageCircle className="h-4 w-4" />
                      Continuar por WhatsApp ({SITE.phoneDisplay})
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-[#E4E9F2] bg-white p-3">
            <div className="flex items-center gap-2">
              <input
                data-testid="chat-input"
                type="text"
                value={input}
                maxLength={500}
                placeholder="Escribe tu pregunta…"
                aria-label="Escribe tu pregunta"
                className="h-11 flex-1 rounded-lg border border-[#DCE5F0] bg-white px-3.5 text-sm text-navy outline-none placeholder:text-steel/70 focus:border-navy-light focus:ring-2 focus:ring-navy-light/30"
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
              />
              <button
                type="button"
                data-testid="chat-send-button"
                aria-label="Enviar mensaje"
                disabled={loading || !input.trim()}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-navy text-white transition-colors hover:bg-navy-light disabled:opacity-50"
                onClick={send}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <p className="mt-2 text-center text-[10px] text-steel">
              Asistente con IA. Si lo prefieres, escríbenos a {SITE.email}.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
